import { FC, useMemo } from 'react';
import { alpha, Box, Button, type SxProps, type Theme, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { useDirection } from '@hooks';
import { useTrans } from '@libs/i18n';
import { ArrowDown } from '@phosphor-icons/react';
const SCROLL_BUTTON_Z_INDEX = 100;
const FADE_IN_ANIMATION = 'fadeIn 0.3s ease-in-out';
const BOTTOM_SPACING = { xs: 100, sm: 120, md: 130 };
const HORIZONTAL_SPACING = { xs: 16, sm: 24, md: 30 };

interface ScrollToBottomButtonProps {
  show: boolean;
  hasNewMessage: boolean;
  isScrollingDown: boolean;
  onClick: () => void;
}

export const ScrollToBottomButton: FC<ScrollToBottomButtonProps> = observer(({
  show,
  hasNewMessage,
  isScrollingDown,
  onClick,
}) => {
  const { t } = useTrans();
  const theme = useTheme();
  const { isRtl } = useDirection();
  const isDarkMode = theme.palette.mode === 'dark';

  const hasText = hasNewMessage || isScrollingDown;
  const buttonText = useMemo(() => {
    if (hasNewMessage) return t('chat.scrollToBottom.newMessage');
    if (isScrollingDown) return t('chat.scrollToBottom.scrollDown');
    return '';
  }, [hasNewMessage, isScrollingDown, t]);

  const containerStyles = useMemo<SxProps<Theme>>(
    () => ({
      position: 'absolute',
      bottom: BOTTOM_SPACING,
      left: isRtl ? { xs: 'auto', sm: HORIZONTAL_SPACING.sm, md: HORIZONTAL_SPACING.md } : HORIZONTAL_SPACING,
      right: isRtl ? HORIZONTAL_SPACING : 'auto',
      zIndex: SCROLL_BUTTON_Z_INDEX,
      pointerEvents: 'none',
      animation: FADE_IN_ANIMATION,
      '@keyframes fadeIn': {
        from: {
          opacity: 0,
          transform: 'translateY(10px)',
        },
        to: {
          opacity: 1,
          transform: 'translateY(0)',
        },
      },
      '& > *': {
        pointerEvents: 'auto',
      },
    }),
    [isRtl],
  );

  const buttonStyles = useMemo<SxProps<Theme>>(
    () => ({
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: { xs: 0.75, sm: 1 },
      px: hasText ? { xs: 1.5, sm: 2 } : { xs: 1, sm: 1.5 },
      py: { xs: 0.75, sm: 1 },
      minWidth: 'auto',
      height: { xs: 36, sm: 40 },
      backgroundColor: isDarkMode
        ? alpha(theme.palette.background.paper, 0.9)
        : alpha(theme.palette.background.paper, 0.95),
      backdropFilter: 'blur(8px)',
      color: 'text.primary',
      border: '1px solid',
      borderColor: isDarkMode
        ? alpha(theme.palette.divider, 0.5)
        : alpha(theme.palette.divider, 0.3),
      borderRadius: 3,
      boxShadow: isDarkMode
        ? '0px 4px 16px rgba(0, 0, 0, 0.4)'
        : '0px 4px 16px rgba(0, 0, 0, 0.1)',
      fontWeight: 500,
      fontSize: { xs: '0.75rem', sm: '0.875rem' },
      textTransform: 'none',
      whiteSpace: 'nowrap',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      '&:hover': {
        backgroundColor: isDarkMode
          ? alpha(theme.palette.background.paper, 0.95)
          : theme.palette.background.paper,
        transform: 'translateY(-2px)',
        boxShadow: isDarkMode
          ? '0px 6px 20px rgba(0, 0, 0, 0.5)'
          : '0px 6px 20px rgba(0, 0, 0, 0.15)',
        borderColor: alpha(theme.palette.primary.main, 0.3),
      },
      '&:active': {
        transform: 'translateY(0)',
        boxShadow: isDarkMode
          ? '0px 2px 8px rgba(0, 0, 0, 0.4)'
          : '0px 2px 8px rgba(0, 0, 0, 0.1)',
      },
      '&:focus-visible': {
        outline: `2px solid ${alpha(theme.palette.primary.main, 0.5)}`,
        outlineOffset: 2,
      },
    }),
    [hasText, isDarkMode, theme],
  );

  const textStyles = useMemo<SxProps<Theme>>(
    () => ({
      order: isRtl ? 2 : 1,
      display: { xs: 'none', sm: 'inline' },
      maxWidth: { sm: '120px', md: 'none' },
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    }),
    [isRtl],
  );

  if (!show) return null;

  return (
    <Box sx={containerStyles}>
      <Button onClick={onClick} aria-label={t('chat.scrollToBottom.ariaLabel')} sx={buttonStyles}>
        {hasText && (
          <Box component="span" sx={textStyles}>
            {buttonText}
          </Box>
        )}
        <ArrowDown
          size={hasText ? 20 : 24}
          weight="bold"
          style={{
            order: isRtl ? 1 : 2,
            flexShrink: 0,
          }}
        />
      </Button>
    </Box>
  );
});
